import * as React from 'react'
import { useOutletContext } from 'react-router-dom'

import { Box, Button, Section, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'

import { observer } from 'mobx-react'

import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import i18n from 'modules/core/lib/i18n'

import { useSurveyShowContext } from 'modules/surveys/pages/Show/hooks'
import type SurveyForm from 'modules/surveys/store/models/Form'
import type Survey from 'modules/surveys/store/models/v2/Survey'

interface Props {
  form: Survey
  canEditSurveyForm: boolean
}

function SurveyBasicInfoV1({ form: surveyForm, canEditSurveyForm }: Props) {
  const { form: surveyV1 } = useOutletContext<{ form: SurveyForm }>()
  const { authorName } = useSurveyShowContext()
  const disabled = !canEditSurveyForm || surveyForm.isFinished

  const form = React.useRef(
    new Form(surveyForm.toJS(), {
      name: 'string',
      description: 'string',
    })
  ).current

  const formName = form.get('name')
  const formDescription = form.get('description')
  const formHasName = !!formName.value
  const { performAction: submit, running } = useAsyncAction(async () => {
    await form.save(surveyForm)
    // TODO: remove later, temporarily fetching v1 to update name in the header
    surveyV1.fetch()
  })

  return (
    <Section.Form
      illustration="settings"
      title={i18n.t('surveys.form.basic_info.title')}
      description={i18n.t('surveys.form.basic_info.subtitle.survey')}
      submitLabel={i18n.t('common.submit')}
    >
      <Box gap="s16">
        <Box gap="s8">
          <TextField
            mandatory
            readonly={disabled}
            value={formName.value}
            onChange={(value) => formName.set(value)}
            label={i18n.t('surveys.form.basic_info.form.labels.name')}
          />
        </Box>
        <Box gap="s8">
          <TextArea
            readonly={disabled}
            value={formDescription.value}
            label={i18n.t('surveys.form.basic_info.form.labels.description')}
            onChange={(value) => formDescription.set(value)}
            placeholder={i18n.t('surveys.form.basic_info.form.description.placeholder')}
          />
        </Box>
        <Box gap="s16">
          <Text.Normal weight="medium" size="200" color="grey1000">
            {i18n.t('common.author')}
          </Text.Normal>
          {authorName ?? '-'}
        </Box>
        {(formName.isDirty || formDescription.isDirty) && (
          <Box alignItems="flexEnd">
            <Button disabled={disabled || running || !formHasName} onClick={submit}>
              {i18n.t('common.save')}
            </Button>
          </Box>
        )}
      </Box>
    </Section.Form>
  )
}

export default observer(SurveyBasicInfoV1)
